'use client';

import { HStack, VStack } from '@terra/ui-styled-system';
import { Text, Menu, Spinner, Tooltip } from '@terra/ui-design-system';
import {
  Tick01Icon,
  CookieIcon,
  Key01Icon,
  SquareLock01Icon,
  RefreshIcon,
  UserIcon,
} from '@terra/ui-icons';
import { useMemo } from 'react';
import type { CachedCredentialDto } from '@terra/shared-contracts-authenticator';
import { type ParsedHeader } from '../../utils/authHeaderDetection';
import { hasReplayInjectableAuthMaterial } from './credentialDataUtils';

type AuthCredentialMenuProps = {
  credentials: CachedCredentialDto[];
  isLoading: boolean;
  targetHeader: ParsedHeader | null;
  onSelectCredential: (credential: CachedCredentialDto) => void;
  onRefreshCredential: (credential: CachedCredentialDto) => void;
  onApplyAllAuth: (credential: CachedCredentialDto) => void;
  refreshingIds?: Set<string>;
  refreshedIds?: Set<string>;
};

/**
 * Context menu content for selecting credentials
 * Matches the toolbar dropdown's look and feel
 */
export function AuthCredentialMenuContent({
  credentials,
  isLoading,
  targetHeader,
  onSelectCredential,
  onRefreshCredential,
  onApplyAllAuth,
  refreshingIds = new Set(),
  refreshedIds = new Set(),
}: AuthCredentialMenuProps) {
  const targetHeaderName = targetHeader?.name.toLowerCase();
  // Group credentials by whether they have relevant auth data
  const { credentialsWithData, credentialsWithoutData } = useMemo(() => {
    const withData: CachedCredentialDto[] = [];
    const withoutData: CachedCredentialDto[] = [];

    credentials.forEach((credential) => {
      const hasRelevantData = (() => {
        if (!targetHeaderName) {
          return hasReplayInjectableAuthMaterial({ credential });
        }

        if (targetHeaderName === 'cookie') {
          return credential.cookies.length > 0;
        }

        if (targetHeaderName === 'authorization') {
          return credential.headers.some(
            (header) => header.key.toLowerCase() === 'authorization',
          );
        }

        const hasMatchingHeader = credential.headers.some(
          (header) => header.key.toLowerCase() === targetHeaderName,
        );
        const hasMatchingSecret = credential.secrets.some(
          (secret) => secret.key.toLowerCase() === targetHeaderName,
        );

        return hasMatchingHeader || hasMatchingSecret;
      })();

      if (hasRelevantData) {
        withData.push(credential);
      } else {
        withoutData.push(credential);
      }
    });

    return {
      credentialsWithData: withData,
      credentialsWithoutData: withoutData,
    };
  }, [credentials, targetHeaderName]);

  if (isLoading) {
    return (
      <Menu.Item value="loading" disabled>
        <HStack gap="2">
          <Spinner size="xs" />
          <Text fontSize="xs" color="fg.muted">
            Loading credentials...
          </Text>
        </HStack>
      </Menu.Item>
    );
  }

  if (credentials.length === 0) {
    return (
      <Menu.Item value="empty" disabled>
        <Text fontSize="xs" color="fg.muted">
          No credentials configured
        </Text>
      </Menu.Item>
    );
  }

  // When a specific auth header is selected, show targeted replacement options
  if (targetHeader) {
    return (
      <>
        {/* Credentials with data */}
        {credentialsWithData.length > 0 && (
          <Menu.ItemGroup>
            <Menu.ItemGroupLabel>
              <HStack gap="1">
                <SquareLock01Icon size={12} />
                <Text>Replace {targetHeader.name}</Text>
              </HStack>
            </Menu.ItemGroupLabel>

            {credentialsWithData.map((credential) => {
              const isRefreshing = refreshingIds.has(credential.id);
              const wasRefreshed = refreshedIds.has(credential.id);

              return (
                <Menu.Item
                  key={credential.id}
                  value={credential.id}
                  onClick={() => onSelectCredential(credential)}
                  disabled={isRefreshing}
                >
                  <HStack gap="2" w="full">
                    {isRefreshing ? (
                      <Spinner size="xs" />
                    ) : wasRefreshed ? (
                      <Tick01Icon size={14} color="var(--colors-fg-success)" />
                    ) : (
                      <UserIcon size={14} />
                    )}
                    <VStack gap="0" alignItems="flex-start" flex="1">
                      <Text fontSize="xs" fontWeight="medium">
                        {credential.username}
                      </Text>
                      <Text fontSize="2xs" color="fg.muted">
                        {credential.role}
                      </Text>
                    </VStack>
                    <CredentialBadges credential={credential} />
                  </HStack>
                </Menu.Item>
              );
            })}
          </Menu.ItemGroup>
        )}

        {/* Refresh section */}
        {credentialsWithoutData.length > 0 && (
          <>
            <Menu.Separator />
            <Menu.ItemGroup>
              <Menu.ItemGroupLabel>
                <HStack gap="1">
                  <RefreshIcon size={12} />
                  <Text>Refresh & Apply</Text>
                </HStack>
              </Menu.ItemGroupLabel>

              {credentialsWithoutData.map((credential) => {
                const isRefreshing = refreshingIds.has(credential.id);
                const wasRefreshed = refreshedIds.has(credential.id);

                return (
                  <Menu.Item
                    key={`refresh-${credential.id}`}
                    value={`refresh-${credential.id}`}
                    onClick={(e) => {
                      e.preventDefault();
                      onRefreshCredential(credential);
                    }}
                    closeOnSelect={false}
                    disabled={isRefreshing}
                  >
                    <HStack gap="2" w="full">
                      {isRefreshing ? (
                        <Spinner size="xs" />
                      ) : wasRefreshed ? (
                        <Tick01Icon
                          size={14}
                          color="var(--colors-fg-success)"
                        />
                      ) : (
                        <RefreshIcon size={14} />
                      )}
                      <VStack gap="0" alignItems="flex-start" flex="1">
                        <Text fontSize="xs">{credential.username}</Text>
                        <Text fontSize="2xs" color="fg.muted">
                          {credential.role} -{' '}
                          {wasRefreshed ? 'Applied' : 'Re-authenticate'}
                        </Text>
                      </VStack>
                    </HStack>
                  </Menu.Item>
                );
              })}
            </Menu.ItemGroup>
          </>
        )}
      </>
    );
  }

  // Default: Show credentials matching toolbar style
  return (
    <>
      {/* Cached credentials section */}
      {credentialsWithData.length > 0 && (
        <Menu.ItemGroup>
          <Menu.ItemGroupLabel>
            <HStack gap="1">
              <SquareLock01Icon size={12} />
              <Text>Apply Credentials</Text>
            </HStack>
          </Menu.ItemGroupLabel>

          {credentialsWithData.map((credential) => {
            const isRefreshing = refreshingIds.has(credential.id);
            const wasRefreshed = refreshedIds.has(credential.id);

            return (
              <Menu.Item
                key={credential.id}
                value={credential.id}
                onClick={() => onApplyAllAuth(credential)}
                disabled={isRefreshing}
              >
                <HStack gap="2" w="full">
                  {isRefreshing ? (
                    <Spinner size="xs" />
                  ) : wasRefreshed ? (
                    <Tick01Icon size={14} color="var(--colors-fg-success)" />
                  ) : (
                    <UserIcon size={14} />
                  )}
                  <VStack gap="0" alignItems="flex-start" flex="1">
                    <Text fontSize="xs" fontWeight="medium">
                      {credential.username}
                    </Text>
                    <Text fontSize="2xs" color="fg.muted">
                      {credential.role}
                    </Text>
                  </VStack>
                  <CredentialBadges credential={credential} />
                </HStack>
              </Menu.Item>
            );
          })}
        </Menu.ItemGroup>
      )}

      {/* Refresh & Apply section */}
      <Menu.Separator />
      <Menu.ItemGroup>
        <Menu.ItemGroupLabel>
          <HStack gap="1">
            <RefreshIcon size={12} />
            <Text>Refresh & Apply</Text>
          </HStack>
        </Menu.ItemGroupLabel>

        {credentials.map((credential) => {
          const isRefreshing = refreshingIds.has(credential.id);
          const wasRefreshed = refreshedIds.has(credential.id);

          return (
            <Menu.Item
              key={`refresh-${credential.id}`}
              value={`refresh-${credential.id}`}
              onClick={(e) => {
                e.preventDefault();
                onRefreshCredential(credential);
              }}
              closeOnSelect={false}
              disabled={isRefreshing}
            >
              <HStack gap="2" w="full">
                {isRefreshing ? (
                  <Spinner size="xs" />
                ) : wasRefreshed ? (
                  <Tick01Icon size={14} color="var(--colors-fg-success)" />
                ) : (
                  <RefreshIcon size={14} />
                )}
                <VStack gap="0" alignItems="flex-start" flex="1">
                  <Text fontSize="xs">{credential.username}</Text>
                  <Text fontSize="2xs" color="fg.muted">
                    {credential.role} -{' '}
                    {wasRefreshed ? 'Applied' : 'Re-authenticate'}
                  </Text>
                </VStack>
              </HStack>
            </Menu.Item>
          );
        })}
      </Menu.ItemGroup>
    </>
  );
}

type CredentialBadgesProps = {
  credential: CachedCredentialDto;
};

/**
 * Small badges showing what auth data a credential has (headers, cookies)
 */
function CredentialBadges({ credential }: CredentialBadgesProps) {
  const hasHeaders = credential.headers.length > 0;
  const hasCookies = credential.cookies.length > 0;

  if (!hasHeaders && !hasCookies) {
    return null;
  }

  return (
    <HStack gap="1">
      {hasHeaders && (
        <Tooltip title={`${credential.headers.length} header(s)`}>
          <Key01Icon size={12} />
        </Tooltip>
      )}
      {hasCookies && (
        <Tooltip title={`${credential.cookies.length} cookie(s)`}>
          <CookieIcon size={12} />
        </Tooltip>
      )}
    </HStack>
  );
}

export type { CachedCredentialDto };
